import { createAsyncThunk } from '@reduxjs/toolkit'

import { COUNTDOWN_SECONDS, Pane, PaneClasses } from '../constants/constants'
import { AppDispatch, RootState } from '../reducers'
import focusSlice from '../reducers/focusSlice'
import modalsSlice from '../reducers/modalsSlice'
import sessionSlice from '../reducers/sessionSlice'
import socialSlice from '../reducers/socialSlice'
import { selectPeerChannel } from '../selectors/blaster-peer-selectors'
import {
  getAudienceFlags,
  selectAudience,
  selectCurrentSessionInfo,
  selectCurrentUsername,
} from '../selectors/session-selectors'
import {
  selectCurrentPlaylistSlug,
  selectCurrentTrackSlug,
} from '../selectors/current-play-selectors'
import keyBindings from '../services/KeyBindings'
import realtime from '../services/RealtimeService'
import trackMixer from '../services/TrackMixer'
import { updateMatchStatus } from './social/leaderboard-actions'

const togglePlay = createAsyncThunk<void, boolean, { state: RootState; dispatch: AppDispatch }>(
  'playAction/togglePlay',
  (isGameOver, { dispatch, getState }) => {
    const state = getState()
    const isShowCountdownClock = state.session.isShowCountdownClock
    const currUsername = selectCurrentUsername(state)
    const compoundChannelSlug = `${currUsername}/default`
    const channel = selectPeerChannel(currUsername)(state)
    const isBroadcasting = channel && channel.isBroadcasting
    const { isPub, isCouch } = getAudienceFlags(selectAudience(state))
    if (trackMixer.isPlaying || isShowCountdownClock) {
      trackMixer.stop()
      if (isBroadcasting) {
        realtime.set(`channels/${compoundChannelSlug}/trackWillPlayAtTimestamp`, 0)
      }
    } else {
      const { currentBlaster, currentMatchSlug: matchSlug } = selectCurrentSessionInfo(state)
      const playlistSlug = selectCurrentPlaylistSlug(state)
      const trackSlug = selectCurrentTrackSlug(state)
      dispatch(activateNextPane({ pane: Pane.PLAY }))
      dispatch(
        socialSlice.actions.toggleTrackShowActiveScore({
          username: currentBlaster,
          matchSlug,
          playlistSlug,
          trackSlug,
          isShowActiveScore: true,
        })
      )
      if (trackMixer.isRewound && trackMixer.isPlayMode) {
        if (isBroadcasting) {
          const willStartAtTimestamp = Date.now() + COUNTDOWN_SECONDS * 1000
          realtime.set(
            `channels/${compoundChannelSlug}/trackWillPlayAtTimestamp`,
            willStartAtTimestamp
          )
        }
        dispatch(sessionSlice.actions.setCountdownSeconds(COUNTDOWN_SECONDS))
        dispatch(sessionSlice.actions.setIsShowCountdownClock(true))
      } else {
        trackMixer.start()
        trackMixer.isLoading = false
      }
    }
    if (trackMixer.isPlayMode) {
      dispatch(updateMatchStatus(-1))
      if (isGameOver && (isPub || isCouch)) {
        dispatch(modalsSlice.actions.toggleMatchupInfo(true))
      }
    }
  }
)

type NextPane = {
  direction?: -1 | 0 | 1
  pane?: Pane
}
const activateNextPane = createAsyncThunk<
  void,
  NextPane,
  { state: RootState; dispatch: AppDispatch }
>('playAction/activateNextPane', ({ direction = 0, pane }, { dispatch, getState }) => {
  const currFocus = getState().focus.currentFocus
  const numPanes = Object.keys(PaneClasses).length
  if (pane === currFocus) {
    return
  }
  const fromPane = pane !== undefined ? pane : currFocus
  let nextContainer = null
  let distance = 1
  while (!nextContainer && distance < numPanes) {
    const nextFocus: Pane = (fromPane + direction * distance++) % numPanes
    nextContainer = document.querySelector(`.${PaneClasses[nextFocus]}`)
    if (nextContainer) {
      keyBindings.onModalContainerRef(nextContainer, true)
      dispatch(focusSlice.actions.setFocus(nextFocus))
    }
  }
})

export { activateNextPane, togglePlay }
